import React, { useCallback } from "react";
import merge from "lodash/merge";
import PlotLegend from "views/components/visualizations/PlotLegend";
import useUserDateTime from "hooks/useUserDateTime";
import { DEFAULT_AXIS_TYPE } from "views/logic/aggregationbuilder/visualizations/XYVisualization";
import assertUnreachable from "logic/assertUnreachable";
import useViewsDispatch from "views/stores/useViewsDispatch";
import { TIMESTAMP_FIELD } from "views/Constants";
import GenericPlot from "./GenericPlot";
import OnZoom from "./OnZoom";
const yLegendPosition = (containerHeight) => {
  if (containerHeight < 150) {
    return -0.6;
  }
  if (containerHeight < 400) {
    return -0.2;
  }
  return -0.14;
};
const mapAxisType = (axisType) => {
  switch (axisType) {
    case "linear":
      return "linear";
    case "logarithmic":
      return "log";
    default:
      return assertUnreachable(axisType, "Unable to parse axis type: ");
  }
};
const defaultSetColor = (chart, colors) => {
  var _a;
  return {
    line: { color: colors.get((_a = chart.originalName) != null ? _a : chart.name) }
  };
};
const isTimestampChart = (config) => config.isTimeline && config.rowPivots[0].fields.length === 1 && config.rowPivots[0].fields[0] === TIMESTAMP_FIELD;
const XYPlot = ({
  axisType = DEFAULT_AXIS_TYPE,
  config,
  chartData,
  effectiveTimerange = void 0,
  setChartColor = defaultSetColor,
  height,
  width,
  plotLayout = {},
  onZoom = void 0,
  onClickMarker = void 0,
  onInitialized = void 0
}) => {
  var _a, _b;
  const { formatTime, userTimezone } = useUserDateTime();
  const yaxis = { fixedrange: true, rangemode: "tozero", tickformat: ",~r", type: mapAxisType(axisType) };
  const defaultLayout = {
    yaxis,
    hovermode: "x"
  };
  if (height) {
    defaultLayout.legend = { y: yLegendPosition(height) };
  }
  const layout = merge({}, defaultLayout, plotLayout);
  const dispatch = useViewsDispatch();
  const _onZoom = useCallback(
    isTimestampChart(config) ? (from, to) => onZoom ? onZoom(from, to, userTimezone) : dispatch(OnZoom(from, to, userTimezone)) : () => true,
    [config.isTimeline, onZoom]
  );
  if (config.isTimeline && effectiveTimerange) {
    const normalizedFrom = formatTime(effectiveTimerange.from, "internal");
    const normalizedTo = formatTime(effectiveTimerange.to, "internal");
    layout.xaxis = merge(layout.xaxis, {
      range: [normalizedFrom, normalizedTo],
      type: "date",
      fixedrange: !isTimestampChart(config)
    });
  } else {
    layout.xaxis = merge(layout.xaxis, {
      fixedrange: true,
      /* disable plotly sorting by setting the type of the xaxis to category */
      type: (_b = (_a = layout.xaxis) == null ? void 0 : _a.type) != null ? _b : config.sort.length > 0 ? "category" : void 0
    });
  }
  return /* @__PURE__ */ React.createElement(PlotLegend, { config, chartData, height, width }, /* @__PURE__ */ React.createElement(
    GenericPlot,
    {
      chartData,
      layout,
      onZoom: _onZoom,
      setChartColor,
      onClickMarker,
      onInitialized
    }
  ));
};
export default XYPlot;
